import {fabric} from "fabric";
import {WrapperObjectHelper} from "./GraphObject/WrapperObjectHelper";
import {viewerConsts} from "../../constants";
import {getTagClassValue} from "./ImageViewerObject";
import ImageViewer from "../ImageViewer/ImageViewer";
import {TagObject} from "../../services/ApiModels/TagObject";

type ImageViewerCanvasObjectAttribute = {
    key: string;
    value: string;
}

type ImageViewerCanvasObjectBase = {
    objectMetadata: {
        id: number,
        text: string,
        label: string,
        metadata: {
            description: string,
            attributes: ImageViewerCanvasObjectAttribute[],
        },
        shape: TagObject["shape"] | undefined,
    },
    isProperObject: boolean,
}

export type ImageViewerCanvasGraphObject = fabric.Rect & ImageViewerCanvasObjectBase & {
    wrappingData: {
        helper: WrapperObjectHelper
    }
}

export type ImageViewerCanvasTagObject = fabric.Rect & ImageViewerCanvasObjectBase;

export type ImageViewerCanvasObject = ImageViewerCanvasTagObject | ImageViewerCanvasGraphObject;

export function isGraph(obj: ImageViewerCanvasObject): obj is ImageViewerCanvasGraphObject {
    return obj.objectMetadata.shape?.shape_type === "graph";
}

export function applyOffset(obj: ImageViewerCanvasObject, offset: { x: number, y: number }) {
    if (isGraph(obj)) {
        obj.wrappingData.helper.applyOffset(offset);
    } else {
        obj.left = (obj.left ?? 0) + offset.x;
        obj.top = (obj.top ?? 0) + offset.y;
    }
}

export function addToCanvas(obj: ImageViewerCanvasObject, canvas: fabric.Canvas) {
    if (isGraph(obj)) {
        obj.wrappingData.helper.addToCanvas();
    } else {
        canvas.add(obj);
    }
}

export function createCanvasObjectFromTagObject(obj: TagObject, iv: ImageViewer): ImageViewerCanvasObject {
    const isGraph = obj.shape?.shape_type === "graph";
    let rect: any;
    if (isGraph) {
        rect = WrapperObjectHelper.buildWrapperObject(obj, iv);
    } else {
        // @ts-ignore
        rect = new fabric.Rect({
            ...viewerConsts.DEFAULT_OBJECT_ARGS,
            width: Math.round(obj.bbox.x2 - obj.bbox.x1),
            height: Math.round(obj.bbox.y2 - obj.bbox.y1),
            // stroke: strokeColor,
            stroke: iv.getColor(getTagClassValue(obj)),
            top: Math.round(obj.bbox.y1),
            left: Math.round(obj.bbox.x1),
            strokeWidth: viewerConsts.STROKE_WIDTH,

            rx: 0.5,
            ry: 0.5,
        });

        if (iv.props.editable) {
            rect.setControlsVisibility({
                mt: false,
                mb: false,
                ml: false,
                mr: false,
                mtr: false,
            });
        } else {
            rect.hasControls = false;
            rect.lockMovementX = true;
            rect.lockMovementY = true;
        }

        let metadata = obj.metadata ?? {};
        metadata.description = metadata.description ?? "";
        metadata.attributes = metadata.attributes ?? [];

        rect.objectMetadata = {
            id: obj.id,
            label: obj.label,
            text: obj.text,
            metadata: metadata,
        };
        rect.isProperObject = true;
    }
    return rect;
}

export class ImageViewerCanvasObjectSmart {
    private readonly origin: ImageViewerCanvasObject;

    constructor(origin: ImageViewerCanvasObject) {
        this.origin = origin;
    }

    getAttributeValue(key: string): string | undefined {
        return this.origin.objectMetadata.metadata.attributes.find(
            x => x.key === key
        )?.value;
    }

    setAttributeValue(key: string, value: string) {
        const attributes = this.origin.objectMetadata.metadata.attributes;
        const attrIdx = attributes.findIndex(x => x.key === key);
        if (attrIdx !== -1) {
            attributes[attrIdx].value = value;
        } else {
            attributes.push({
                key: key,
                value: value
            });
        }
    }

    deleteAttribute(key: string) {
        const attributes = this.origin.objectMetadata.metadata.attributes;
        this.origin.objectMetadata.metadata.attributes = attributes.filter(x => x.key !== key);
    }
}
